import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { useParams } from "react-router-dom";

import { ReactComponent as Email } from "@assets/icons/email.svg";
import { ReactComponent as Linkedin } from "@assets/icons/linkedin.svg";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import { useGetCompanyQuery } from "@/pages/Private/redux/companies/companies.api";
import { useSlotsAvailableQuery } from "@/pages/Private/redux/unipile/unipile.api";
import { SvgIcon } from "@/components/Icon/SvgIcon";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { PermissionCheck } from "@/components";
import { PermissionRoles } from "@/enum";
import { useSnackBar } from "@/utils/useSnackbar";

import { UsersTable } from "../../../Users/components/UsersTable";
import { EditEmailModal } from "../../../Users/components/EditEmailModal";
import { BonusBadge } from "../../components/LicencesCell";
import { ManageLicencesModal } from "../../components/ManageLicencesModal";

export const UserOverview: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`users.${key}`);
	const tl = (key: string) => t(`companies.licences.${key}`);
	const { id } = useParams();
	const { user } = useAppSelector(authSelector);

	const companyId = id ? +id : user?.company?.id || 0;
	const { data: company } = useGetCompanyQuery(companyId);
	const [isEditEmailModalOpen, setIsEditEmailModalOpen] = useState<boolean>(false);
	const [isLicencesModalOpen, setIsLicencesModalOpen] = useState<boolean>(false);
	const { data: slotData } = useSlotsAvailableQuery(id ? +id : undefined);
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	// Staff-only, like the row actions on the Companies table: the API refuses everyone outside
	// company 1 regardless of role (PREC-4063).
	const manageLicencesButton = (
		<PermissionCheck requiredPermissions={[PermissionRoles.COOKIE]}>
			<Button
				color={ButtonColor.ACTION_SECONDARY}
				size={ButtonSize.S}
				testId="manage-licences"
				title={tl("manage")}
				onClick={() => setIsLicencesModalOpen(true)}
			/>
		</PermissionCheck>
	);

	return (
		<>
			<div className="flex flex-row mb-4">
				<div className="flex items-end justify-between w-full mb-4">
					<div className="flex-grow">
						<h3 className="mb-2 text-lg font-semibold leading-7">{ts("title")}</h3>
						<h3 className="mb-3 text-sm font-normal">{company?.name}</h3>
					</div>
				</div>
			</div>

			<div className="flex flex-row items-center gap-4">
				<div className="mb-4 border border-border-light rounded-xl gap-4 p-4 w-[400px] flex flex-col justify-between">
					<div className="flex flex-row items-center gap-1">
						<SvgIcon className="w-[20px] h-[20px]" svgIcon={Linkedin} />

						<div className="text-sm font-normal">Sending Users</div>
					</div>

					<div className="flex flex-row items-end justify-between gap-1">
						<div className="flex flex-col items-start gap-1">
							<div className="flex items-center gap-2 text-smd font-normal text-text">
								{slotData?.slotAmount} Seats
								<BonusBadge amount={slotData?.linkedinsBonusAmount ?? 0} />
							</div>
							<div className="text-sm font-normal text-text-meta">
								{slotData?.slotsAvailable} / {slotData?.slotAmount} seats used
							</div>
						</div>
						{manageLicencesButton}
					</div>
				</div>
				<div className="mb-4 border border-border-light rounded-xl gap-4 p-4 w-[400px] flex flex-col justify-between">
					<div className="flex flex-row items-center gap-1">
						<SvgIcon className="w-[20px] h-[20px]" svgIcon={Email} />

						<div className="text-sm font-normal">Additional Email Addresses</div>
					</div>

					<div className="flex flex-row items-end justify-between gap-1">
						<div className="flex flex-col items-start gap-1">
							<div className="flex items-center gap-2 text-smd font-normal text-text">
								{slotData?.emailsAmount} Emails
								<BonusBadge amount={slotData?.emailsBonusAmount ?? 0} />
							</div>
							<div className="text-sm font-normal text-text-meta">
								{slotData?.emailsUsed} / {slotData?.emailsAmount} emails used
							</div>
						</div>
						<div className="flex flex-col items-end gap-2">
							{slotData?.hasStripeSubscription ? (
								<div className="text-sm font-normal text-text-meta w-fit">
									<Button
										title="Edit number of Email accounts"
										onClick={() => setIsEditEmailModalOpen(true)}
									/>
								</div>
							) : (
								""
							)}
							{manageLicencesButton}
						</div>
					</div>
				</div>
			</div>

			<UsersTable />
			{isEditEmailModalOpen && (
				<EditEmailModal
					baseAmount={slotData?.emailBaseAmount ?? 0}
					bonusAmount={slotData?.emailsBonusAmount ?? 0}
					companyId={id ? +id : undefined}
					isOpen={isEditEmailModalOpen}
					paidAmount={slotData?.emailsPaidAmount ?? 0}
					pendingAmount={slotData?.emailsPendingAmount}
					pendingEffectiveAt={slotData?.emailsPendingEffectiveAt}
					setIsOpen={setIsEditEmailModalOpen}
				/>
			)}
			{isLicencesModalOpen && company && (
				<ManageLicencesModal
					companyId={companyId}
					companyName={company.name}
					isOpen={isLicencesModalOpen}
					setIsOpen={setIsLicencesModalOpen}
					onSaved={() => handleOpenSnackbar(tl("saved"))}
				/>
			)}
			{renderMessageSnackbar()}
		</>
	);
};
